import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  scopeLevel: 'platform' | 'partner' | 'tenant';
  role: 'admin' | 'analyst' | 'viewer';
  partnerId: number | null;
  tenantId: number | null;
  totpEnabled?: boolean;
}

// Esito del primo passo di login: se l'utente ha il 2FA attivo, il backend non emette ancora un
// token di sessione ma un pendingToken di vita breve da completare con verifyTotp() — vedi
// backend/src/routes/auth.ts.
type LoginResult = { requires2fa: false } | { requires2fa: true; pendingToken: string };

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  // Ruoli applicati dal backend dal 2026-10-17 (middleware/auth.ts:requireWriteRole): admin
  // scrive tutto nel proprio scope, analyst gestisce case/alert/regole/whitelist/asset, viewer
  // legge. La UI nasconde i comandi che il backend rifiuterebbe comunque con 403.
  isAdmin: boolean;
  canEdit: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyTotp: (pendingToken: string, code: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const applyToken = useCallback((token: string | null) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('soc_token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    applyToken(token);
    axios
      .get('/api/auth/me')
      .then(res => setUser(res.data.data as AuthUser))
      .catch(() => {
        localStorage.removeItem('soc_token');
        applyToken(null);
      })
      .finally(() => setIsLoading(false));
  }, [applyToken]);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const res = await axios.post('/api/auth/login', { email, password });
    if (res.data.data.requires2fa) {
      return { requires2fa: true, pendingToken: res.data.data.pendingToken };
    }
    const { token, user: authUser } = res.data.data;
    localStorage.setItem('soc_token', token);
    applyToken(token);
    setUser(authUser);
    return { requires2fa: false };
  }, [applyToken]);

  const verifyTotp = useCallback(async (pendingToken: string, code: string) => {
    const res = await axios.post('/api/auth/2fa/verify-login', { pendingToken, code });
    const { token, user: authUser } = res.data.data;
    localStorage.setItem('soc_token', token);
    applyToken(token);
    setUser(authUser);
  }, [applyToken]);

  const logout = useCallback(() => {
    localStorage.removeItem('soc_token');
    applyToken(null);
    setUser(null);
  }, [applyToken]);

  return (
    <AuthContext.Provider value={{
      user, isLoading, isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      canEdit: user?.role === 'admin' || user?.role === 'analyst',
      login, verifyTotp, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
